import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, firstValueFrom, Observable, Subscription } from 'rxjs';
import { LabelContext } from 'src/app/shared/framework/labels/models/LabelContext';
import { LabelService } from 'src/app/shared/framework/labels/services/label.service';
import { GeneralActor } from 'src/app/shared/general-enums/GeneralActor';
import { Picklist, PicklistItem } from '../../../user-defined-fields/models/Picklist';
import { PicklistService } from '../../../user-defined-fields/services/picklist.service.';
import { FieldType } from '../../enums/FieldType';
import { LogKind } from '../../enums/LogKind';
import {
  CompositeFieldLogDto,
  CompositeRowVm,
  CustomFieldTabContentVm,
  CustomFieldVm,
  FieldLogDto
} from '../../models/CustomFieldContent';
import { CustomFieldService } from '../../services/custom-field.service';

@Component({
  selector: 'app-custom-field-content',
  templateUrl: './custom-field-content.component.html',
  styleUrls: ['./custom-field-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomFieldContentComponent implements OnInit, OnDestroy {

  public FieldType = FieldType;
  public LogKind = LogKind;

  public loading = false;
  public loadingLog = false;
  public pendingChanges = false;
  public saving = false;

  public vm: CustomFieldTabContentVm;
  public picklists: Picklist[] = [];

  public logField: CustomFieldVm | null = null;
  public scalarLog: FieldLogDto | null = null;
  public compositeLog: CompositeFieldLogDto | null = null;
  public compositeLogsAllCollapsed = false;

  public fieldsWithChangedValueAndDateEffective: CustomFieldVm[] = [];
  public changedCompositeRowsWithDateEffective: { field: CustomFieldVm; row: CompositeRowVm }[] = [];

  public role: GeneralActor = GeneralActor.Employee;

  public now: Date = new Date();
  public customFieldDateMinDate: NgbDateStruct = { year: 1950, month: 1, day: 1 };

  @Input() set tabId(value: number) {
    this._tabId = value;
    this.tryLoadData();
  }

  get tabId(): number {
    return this._tabId;
  }

  @Input() set employeeId(value: number) {
    if (this._employeeId !== value) {
      this._employeeId = value;
      this.tryLoadData();
    }
  }

  get employeeId(): number {
    return this._employeeId;
  }

  @Input() set candidateId(value: number) {
    if (this._candidateId !== value) {
      this._candidateId = value;
      this.tryLoadData();
    }
  }

  get candidateId(): number {
    return this._candidateId;
  }

  @ViewChild('form', { static: true }) form: NgForm;

  private subscriptions: Subscription[] = [];
  private _labelContext: LabelContext;
  private _tabId: number;
  private _employeeId: number;
  private _candidateId: number;

  constructor(
    private activatedRoute: ActivatedRoute,
    private picklistService: PicklistService,
    private customFieldService: CustomFieldService,
    private labelService: LabelService,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private _change: ChangeDetectorRef,
  ) {
    if (localStorage.getItem('corporatehr_view') === '1') {
      this.role = GeneralActor.CorporateHR;
    } else if (localStorage.getItem('localhr_view') === '1') {
      this.role = GeneralActor.LocalHR;
    } else if (localStorage.getItem('delegate_view') === '1' || localStorage.getItem('matrixmanager_view') === '1') {
      // Delegated and matrix managers use the manager custom-field rights profile.
      this.role = GeneralActor.Manager;
    } else if (localStorage.getItem('manager_view') === '1') {
      this.role = GeneralActor.Manager;
    } else if (localStorage.getItem('candidate_view') === '1') {
      this.role = GeneralActor.Candidate;
    } else {
      this.role = GeneralActor.Employee;
    }
  }

  async ngOnInit() {
    this._labelContext = await firstValueFrom(this.labelService.getContext('general'));
    this.getDependencies();
  }

  public get canEdit(): boolean {
    return !!this.vm?.tab?.canEdit;
  }

  public checkTime($event: KeyboardEvent, startElement: boolean = false) {
    const input = $event.target as HTMLInputElement;
    const key = $event.key;
    const allowedKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Backspace', 'Delete'];

    if (allowedKeys.includes(key)) {
      return;
    }

    if (!/^\d$/.test(key)) {
      $event.preventDefault();
      return;
    }

    setTimeout(() => {
      if (input.value.length === 2) {
        if (input.ariaLabel === 'Hours') {
          const minutesInput = input.parentElement?.parentElement?.querySelector<HTMLInputElement>('input[aria-label="Minutes"]');
          minutesInput?.focus();
          minutesInput?.select();
        } else if (input.ariaLabel === 'Minutes' && startElement) {
          const endHoursInput = document.querySelector<HTMLInputElement>('#faceToFaceEndTime input[aria-label="Hours"]');
          endHoursInput?.focus();
          endHoursInput?.select();
        }
      }

      this._change.markForCheck();
    });
  }

  public selectInput(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    input.select();
    this.markPendingChanges();
  }

  public isMultipleChoiceItemChecked(value: any, multipleChoiceItem: PicklistItem) {
    return Array.isArray(value) && value.some((candidate: number) => candidate == multipleChoiceItem.picklistItemId);
  }

  public clickScalarMultipleChoiceItem(field: CustomFieldVm, multipleChoiceItem: PicklistItem) {
    if (!field.scalarValue) {
      return;
    }

    field.scalarValue.value = this.toggleMultipleChoiceSelection(field.scalarValue.value, multipleChoiceItem.picklistItemId);
    this.markPendingChanges();
  }

  public clickCompositeMultipleChoiceItem(cellValue: any, row: CompositeRowVm, childField: any, multipleChoiceItem: PicklistItem) {
    const cell = this.customFieldService.getCompositeCell(row, childField.fieldId);
    cell.value = this.toggleMultipleChoiceSelection(cellValue, multipleChoiceItem.picklistItemId);
    this.markPendingChanges();
  }

  public addCompositeValue(field: CustomFieldVm) {
    field.composite?.rows.push(this.customFieldService.createCompositeRow(field));
    this.markPendingChanges();
  }

  public deleteCompositeValue(field: CustomFieldVm, row: CompositeRowVm) {
    if (!field.composite) {
      return;
    }

    if (row.rowId) {
      row.deleted = true;
    } else {
      field.composite.rows = field.composite.rows.filter(candidate => candidate !== row);
    }

    this.markPendingChanges();
  }

  public canAddItem(field: CustomFieldVm) {
    const visibleRows = this.getVisibleCompositeRows(field).length;
    return !field.maximumAmount || field.maximumAmount > visibleRows;
  }

  public canDeleteItem(field: CustomFieldVm) {
    const visibleRows = this.getVisibleCompositeRows(field).length;
    return !field.minimumAmount || field.minimumAmount < visibleRows;
  }

  public canChangeAddOrDeleteItem(field: CustomFieldVm | any, selectedValues: number[] | undefined, multipleChoiceItem: PicklistItem): boolean {
    const selectedCount = selectedValues?.length ?? 0;
    const isChecked = selectedValues?.some(candidate => candidate === multipleChoiceItem.picklistItemId);

    if (isChecked) {
      return !field.minimumAmount || selectedCount > field.minimumAmount;
    }

    return !field.maximumAmount || selectedCount < field.maximumAmount;
  }

  public getFieldValidationPattern(field: any) {
    return this.customFieldService.getContentFieldValidationPattern(field);
  }

  public preventDecimalInput(event: KeyboardEvent): void {
    if (event.key === '.' || event.key === ',' || event.key === 'e' || event.key === '-' || event.key === '+') {
      event.preventDefault();
    }
  }

  public getVisibleCompositeRows(field: CustomFieldVm): CompositeRowVm[] {
    return (field.composite?.rows ?? []).filter(row => !row.deleted);
  }

  public trackCategories(index: number, item: any) {
    return item.categoryId;
  }

  public trackFields(index: number, item: CustomFieldVm) {
    return item.fieldId;
  }

  public trackChildFields(index: number, item: any) {
    return item.fieldId;
  }

  public trackCompositeRows(index: number, item: CompositeRowVm) {
    return item.rowId ?? item.clientRowId;
  }

  public getCompositeCell(row: CompositeRowVm, fieldId: number) {
    return this.customFieldService.getCompositeCell(row, fieldId);
  }

  public getListValueAsLabels(field: CustomFieldVm | any, value?: string | null) {
    if (!value) {
      return '';
    }

    const picklist = field.picklist ?? this.picklists.find(candidate => candidate.listTypeID == field.picklistId);
    return picklist?.items.find(candidate => candidate.picklistItemId == Number(value))?.label ?? '';
  }

  public getMultipleChoiceValueAsLabels(field: CustomFieldVm | any, value?: string | null): string {
    if (!value) {
      return '';
    }

    const selectedIds = value
      .split(',')
      .map(candidate => Number(candidate.trim()))
      .filter(candidate => !isNaN(candidate));

    const picklist = field.picklist ?? this.picklists.find(candidate => candidate.listTypeID == field.picklistId);
    if (!picklist?.items?.length) {
      return selectedIds.join('<br/>');
    }

    return picklist.items
      .filter(item => selectedIds.includes(item.picklistItemId))
      .map(item => item.label)
      .join('<br/>');
  }

  public getLogValueAsLabel(field: CustomFieldVm | any, value?: string | null): string {
    if (value === undefined || value === null || value.toString().trim() === '') {
      return '';
    }

    if (!field) {
      return value;
    }

    if (field.fieldType == FieldType.Time) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      }

      return value.toString().substring(0, 5);
    }

    if (field.fieldType == FieldType.List) {
      return this.getListValueAsLabels(field, value);
    }

    if (field.fieldType == FieldType.MultipleChoicePicklist) {
      return this.getMultipleChoiceValueAsLabels(field, value);
    }

    if (field.fieldType == FieldType.CheckboxYesOrNo) {
      return value == '1'
        ? this._labelContext.get('yes')?.labelText ?? 'Ja'
        : this._labelContext.get('no')?.labelText ?? 'Nee';
    }

    return value;
  }

  public getFieldPreviewValue(field: CustomFieldVm): string {
    if (!field.scalarValue) {
      return '';
    }

    const serialized = this.customFieldService.formatContentValue(field.fieldType, field.scalarValue.value);
    return this.getLogValueAsLabel(field, serialized);
  }

  public getCompositeRowPreview(field: CustomFieldVm, row: CompositeRowVm): string[] {
    return (field.composite?.childFields ?? []).map(childField => {
      const cell = this.getCompositeCell(row, childField.fieldId);
      const serialized = this.customFieldService.formatContentValue(childField.fieldType, cell.value);
      return `${childField.name}: ${this.getLogValueAsLabel(childField, serialized)}`;
    });
  }

  public getCompositeLogChildField(childFieldId: number) {
    return this.logField?.composite?.childFields?.find(childField => childField.fieldId === childFieldId);
  }

  public toggleCompositeLog(group: { collapsed?: boolean }) {
    group.collapsed = !group.collapsed;
    this.compositeLogsAllCollapsed = (this.compositeLog?.groups ?? []).every(candidate => candidate.collapsed);
    this._change.markForCheck();
  }

  public toggleAllCompositeLogs() {
    this.compositeLogsAllCollapsed = !this.compositeLogsAllCollapsed;
    for (const group of this.compositeLog?.groups ?? []) {
      group.collapsed = this.compositeLogsAllCollapsed;
    }

    this._change.markForCheck();
  }

  public async viewLog(modalContent: TemplateRef<any>, field: CustomFieldVm) {
    this.logField = field;
    this.scalarLog = null;
    this.compositeLog = null;
    this.loadingLog = true;
    this._change.markForCheck();

    const request$ = field.fieldType === FieldType.Composite
      ? this.customFieldService.getCompositeFieldLog(this.tabId, field.fieldId, this.employeeId, this.candidateId, this.role)
      : this.customFieldService.getScalarFieldLog(this.tabId, field.fieldId, this.employeeId, this.candidateId, this.role);

    try {
      const response = await firstValueFrom(request$ as Observable<any>);
      if (field.fieldType === FieldType.Composite) {
        this.compositeLog = response as CompositeFieldLogDto;
        for (const group of this.compositeLog.groups) {
          (group as any).collapsed = false;
        }
        this.compositeLogsAllCollapsed = false;
      } else {
        this.scalarLog = response as FieldLogDto;
      }

      this.loadingLog = false;
      this._change.markForCheck();

      this.modalService.open(modalContent, { size: 'xl', scrollable: true }).result.finally(() => {
        this.logField = null;
        this.scalarLog = null;
        this.compositeLog = null;
      });
    } catch {
      this.loadingLog = false;
      this.logField = null;
      this._change.markForCheck();
    }
  }

  public saveCustomFieldsPerTab(dateEffectiveModal: TemplateRef<any>) {
    this.fieldsWithChangedValueAndDateEffective = [];
    this.changedCompositeRowsWithDateEffective = [];

    for (const category of this.vm?.categories ?? []) {
      for (const field of category.fields) {
        if (!field.useDateEffective || !field.canEdit) {
          continue;
        }

        if (field.fieldType !== FieldType.Composite && field.scalarValue && this.customFieldService.didScalarValueChange(field)) {
          const normalizedValue = this.customFieldService.normalizeComparableValue(field.fieldType, field.scalarValue.value);
          if (normalizedValue !== null) {
            field.scalarValue.dateEffective = field.scalarValue.dateEffective ?? new Date();
            this.fieldsWithChangedValueAndDateEffective.push(field);
          }
          continue;
        }

        if (field.fieldType === FieldType.Composite) {
          for (const row of this.getVisibleCompositeRows(field)) {
            if (!this.customFieldService.didCompositeRowChange(field, row) || this.customFieldService.isCompositeRowEmpty(field, row)) {
              continue;
            }

            row.dateEffective = row.dateEffective ?? new Date();
            this.changedCompositeRowsWithDateEffective.push({ field, row });
          }
        }
      }
    }

    if (this.fieldsWithChangedValueAndDateEffective.length || this.changedCompositeRowsWithDateEffective.length) {
      this.modalService.open(dateEffectiveModal, { backdrop: 'static', size: 'xl' }).result.then(() => {
        this.saveCustomFieldValues();
      }, () => {
        for (const field of this.fieldsWithChangedValueAndDateEffective) {
          if (field.scalarValue) {
            field.scalarValue.dateEffective = field.scalarValue.originalDateEffective ?? null;
          }
        }

        for (const entry of this.changedCompositeRowsWithDateEffective) {
          entry.row.dateEffective = entry.row.originalDateEffective ?? null;
        }
      }).finally(() => {
        this.fieldsWithChangedValueAndDateEffective = [];
        this.changedCompositeRowsWithDateEffective = [];
      });
    } else {
      this.saveCustomFieldValues();
    }
  }

  public isFutureDateEffective(dateEffective: Date | string | null | undefined): boolean {
    if (!dateEffective) {
      return false;
    }

    const date = new Date(dateEffective);
    const today = new Date();

    date.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return date > today;
  }

  ngOnDestroy(): void {
    for (const subsc of this.subscriptions) {
      subsc.unsubscribe();
    }
  }

  private getDependencies() {
    const dependencies: Observable<any>[] = [
      this.picklistService.getPicklists(),
    ];

    this.subscriptions.push(
      combineLatest([...dependencies]).subscribe(([picklists]) => {
        this.picklists = picklists;
        this.tryLoadData();
      })
    );
  }

  private tryLoadData() {
    if (!this._tabId) return;
    if (!this._candidateId && !this._employeeId) return;

    this.getData();
  }

  private getData() {
    this.loading = true;
    this._change.detectChanges();

    this.subscriptions.push(
      this.customFieldService.getCustomFieldTabContent(this.tabId, this.employeeId, this.candidateId, this.role).subscribe(dto => {
        this.vm = dto ? this.customFieldService.mapContentDtoToVm(dto, this.picklists) : null;
        this.pendingChanges = false;
        this.form.form.markAsPristine();
        this.setupFormChangesListener();
        this.loading = false;
        this._change.detectChanges();
      })
    );
  }

  private saveCustomFieldValues() {
    if (!this.vm) {
      return;
    }

    const command = this.customFieldService.buildSaveCommand(this.vm);
    if (!command.scalarChanges.length && !command.compositeChanges.length) {
      this.pendingChanges = false;
      this.form.form.markAsPristine();
      this._change.markForCheck();
      return;
    }

    this.saving = true;
    this._change.detectChanges();

    this.subscriptions.push(
      this.customFieldService.saveCustomFieldContentCommand(command, this.role).subscribe(dto => {
        this.vm = this.customFieldService.mapContentDtoToVm(dto, this.picklists);
        this.saving = false;
        this.pendingChanges = false;
        this.form.form.markAsPristine();
        this._change.detectChanges();

        const label = this._labelContext.get('savedsuccessfully').labelText;
        this.toastr.clear();
        this.toastr.success(label, undefined, { timeOut: 1000 });
      })
    );
  }

  private setupFormChangesListener() {
    this.subscriptions.push(
      this.form.form.valueChanges.subscribe(() => {
        this.pendingChanges = this.form.form.dirty;
        this._change.markForCheck();
      })
    );
  }

  private toggleMultipleChoiceSelection(currentValue: number[] | undefined, itemId: number): number[] {
    const selected = currentValue ?? [];
    return selected.some(candidate => candidate === itemId)
      ? selected.filter(candidate => candidate !== itemId)
      : [...selected, itemId];
  }

  public markPendingChanges() {
    this.pendingChanges = true;
    this.form.form.markAsDirty();
    this._change.markForCheck();
  }
}
